/**
 * People and roles.
 *
 * The role picker shows what each role can actually do, taken from the same
 * matrix the backend enforces with — so the consequence of a change is visible
 * before it is made, rather than described in a tooltip that drifts out of
 * date.
 *
 * The list finds a person; the person opens beside it with their department,
 * their password state and their role, each changed from there.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Department, type Location, type User } from '../api'
import { ROLE_LABELS, useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'
import AddPerson, { generatePassword } from './AddPerson'

interface RoleInfo {
  role: string
  permissions: string[]
}

const ROLES = ['admin', 'finance_head', 'book_keeper', 'employee']
const VISIBILITIES = ['self', 'locations', 'all']

/** The verbs that mean "can move or authorise money". Mirrors the backend. */
const TRANSACTIONAL = new Set([
  'create',
  'edit',
  'submit',
  'approve',
  'reject',
  'post',
  'void',
  'release',
  'reconcile',
  'categorize',
  'prepare',
  'close',
])

function summarise(permissions: string[]) {
  const approvals = permissions.filter((p) => p.endsWith('.approve')).length
  const transactional = permissions.filter(
    (p) => TRANSACTIONAL.has(p.split('.').pop()!) && !p.startsWith('org.'),
  ).length
  return { total: permissions.length, approvals, transactional }
}

type Tab = 'active' | 'inactive' | 'all' | 'roles'

export default function People() {
  const { can, me } = useAuth()
  const queryClient = useQueryClient()

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/org/users') })
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleInfo[]>('/org/roles'),
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
  })

  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/org/departments'),
    enabled: can('org.department.view'),
  })
  // The department a person works in defaults what they raise, and a request
  // raised for another department is flagged: choosing a department chooses
  // the approver.
  const setDepartment = useMutation({
    mutationFn: (vars: { user: User; departmentId: string }) =>
      api.put<User>(`/org/users/${vars.user.id}/department`, {
        department_id: vars.departmentId || null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const [reissued, setReissued] = useState<{ user: User; password: string } | null>(null)

  const reissue = useMutation({
    mutationFn: (vars: { user: User; password: string }) =>
      api.post<User>(`/org/users/${vars.user.id}/first-password`, {
        password: vars.password,
      }),
    onSuccess: (user, vars) => {
      setReissued({ user, password: vars.password })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  //: Replacing a password its owner has set. Loud by design: a written reason
  //: goes on the audit trail, every session the owner holds is revoked, and the
  //: new password opens only the screen that replaces it. The same banner as a
  //: first password shows what to hand over.
  const reset = useMutation({
    mutationFn: (vars: { user: User; password: string; reason: string }) =>
      api.post<User>(`/org/users/${vars.user.id}/reset-password`, {
        password: vars.password,
        reason: vars.reason,
      }),
    onSuccess: (user, vars) => {
      setReissued({ user, password: vars.password })
      setResetReason('')
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
  const [resetReason, setResetReason] = useState('')

  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [nextRole, setNextRole] = useState('employee')
  const [nextVisibility, setNextVisibility] = useState('self')
  const [reason, setReason] = useState('')
  const [tab, setTab] = useState<Tab>('active')
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [onlyFirstPassword, setOnlyFirstPassword] = useState(false)

  const rows = users.data ?? []
  const opened = rows.find((u) => u.id === open)

  const changeRole = useMutation({
    mutationFn: () =>
      api.put<User>(`/org/users/${opened!.id}/role`, {
        role: nextRole,
        visibility: nextVisibility,
        scope_location_ids: opened!.scope_location_ids,
        reason,
      }),
    onSuccess: () => {
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const departmentName = (id: string | null) =>
    departments.data?.find((d) => d.id === id)?.name ?? null
  const locationName = (id: string | null) => locations.data?.find((l) => l.id === id)?.name ?? null
  const shown = rows.filter(
    (u) =>
      (tab === 'all' || (tab === 'active' ? u.is_active : !u.is_active)) &&
      (!role || u.role === role) &&
      (!onlyFirstPassword || u.must_change_password) &&
      matches(search, u.display_name, u.email, departmentName(u.department_id)),
  )
  const paged = usePaged(shown, `${tab}|${role}|${search}|${onlyFirstPassword}`)

  if (users.isLoading) return <Loading what="Loading people" />

  const active = rows.filter((u) => u.is_active)
  const approvers = active.filter((u) => u.role === 'finance_head' || u.role === 'admin').length
  const paymentApprovers = active.filter((u) => u.role === 'finance_head').length
  const firstPasswords = active.filter((u) => u.must_change_password)

  const openPerson = (u: User) => {
    setOpen(u.id)
    setNextRole(u.role)
    setNextVisibility(u.visibility)
    setReason('')
    setResetReason('')
  }

  return (
    <>
      <ErrorNote error={users.error} />

      {can('org.user.configure') && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => setAdding(true)}>
            Add somebody
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            they set their own password on first sign-in
          </span>
        </div>
      )}

      {reissued && !opened && (
        <div className="bank-result" style={{ padding: '0 0 12px' }}>
          <PasswordBanner reissued={reissued} />
          <button className="quiet small" onClick={() => setReissued(null)}>
            Dismiss
          </button>
        </div>
      )}

      {paymentApprovers < 2 && (
        <Note kind="warn">
          Only {paymentApprovers} active Finance Head{paymentApprovers === 1 ? '' : 's'}. Separation
          of duties needs at least two people who can approve payments — otherwise the emergency
          override becomes the everyday route and the control is decorative. ({approvers} account
          {approvers === 1 ? '' : 's'} can approve something.)
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Active people"
          value={String(active.length)}
          note={`${rows.length - active.length} deactivated`}
        />
        <Stat
          label="Finance Heads"
          value={String(paymentApprovers)}
          note="the people who can approve a payment"
        />
        <Stat
          label="Still on a first password"
          value={String(firstPasswords.length)}
          note={
            firstPasswords.length
              ? 'whoever set it can sign in as them until they change it'
              : 'everybody has chosen their own'
          }
        />
        <Stat
          label="Without a department"
          value={String(active.filter((u) => !u.department_id).length)}
          note="their requests carry no department by default"
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'active', label: 'Active', count: active.length },
              {
                key: 'inactive',
                label: 'Deactivated',
                count: rows.length - active.length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
              { key: 'roles', label: 'What each role can do' },
            ]}
            value={tab}
            onChange={setTab}
          />
          {tab !== 'roles' && (
            <div className="appr-filters">
              <input
                type="search"
                placeholder="Name, email, department"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search people"
              />
              <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
                <option value="">Every role</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r] ?? r}
                  </option>
                ))}
              </select>
              <label className="appr-check">
                <input
                  type="checkbox"
                  checked={onlyFirstPassword}
                  onChange={(e) => setOnlyFirstPassword(e.target.checked)}
                />
                only first passwords
              </label>
            </div>
          )}
        </header>
        <div className="body tight">
          {tab === 'roles' ? (
            <>
              <div className="faint" style={{ padding: '10px 16px' }}>
                Read from the matrix the backend enforces with, so it cannot drift from what each
                role is actually allowed.
              </div>
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th className="num">People</th>
                    <th className="num">Permissions</th>
                    <th className="num">Can approve</th>
                    <th className="num">Can move money</th>
                  </tr>
                </thead>
                <tbody>
                  {(roles.data ?? []).map((r) => {
                    const s = summarise(r.permissions)
                    return (
                      <tr key={r.role}>
                        <td className="strong">{ROLE_LABELS[r.role] ?? r.role}</td>
                        <td className="num">{active.filter((u) => u.role === r.role).length}</td>
                        <td className="num">{s.total}</td>
                        <td className="num">{s.approvals}</td>
                        <td className="num">
                          {s.transactional === 0 ? (
                            <span className="pill draft">no</span>
                          ) : (
                            s.transactional
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </>
          ) : shown.length === 0 ? (
            <div className="empty">No one matches that.</div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Sees</th>
                  <th>Home</th>
                  <th>Department</th>
                  <th>Password</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((u) => (
                  <tr key={u.id} {...rowProps(() => openPerson(u), open === u.id)}>
                    <td>
                      <div className="strong">
                        {u.display_name}
                        {u.id === me?.user_id && <span className="faint"> (you)</span>}
                      </div>
                      <div className="cell-sub mono">{u.email}</div>
                    </td>
                    <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                    <td className="muted">{u.visibility}</td>
                    <td className="muted">
                      {locationName(u.home_location_id) ?? <span className="faint">—</span>}
                    </td>
                    <td className="muted">
                      {departmentName(u.department_id) ?? <span className="faint">—</span>}
                    </td>
                    <td>
                      {u.must_change_password ? (
                        <span className="pill advisory">first password</span>
                      ) : (
                        <span className="muted">their own</span>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${u.is_active ? 'approved' : 'draft'}`}>
                        {u.is_active ? 'active' : 'deactivated'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {tab !== 'roles' && <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />}
      </section>

      {opened && (
        <SidePanel
          kicker={ROLE_LABELS[opened.role] ?? opened.role}
          title={
            <>
              {opened.display_name}
              {opened.id === me?.user_id && <span className="faint"> (you)</span>}
            </>
          }
          sub={<span className="mono">{opened.email}</span>}
          badge={
            <span className={`pill ${opened.is_active ? 'approved' : 'draft'}`}>
              {opened.is_active ? 'active' : 'deactivated'}
            </span>
          }
          onClose={() => setOpen(null)}
        >
          {reissued && reissued.user.id === opened.id && <PasswordBanner reissued={reissued} />}

          <section>
            <dl className="appr-facts">
              <div>
                <dt>Role</dt>
                <dd>{ROLE_LABELS[opened.role] ?? opened.role}</dd>
              </div>
              <div>
                <dt>Sees</dt>
                <dd>
                  {opened.visibility}
                  {opened.visibility === 'locations' && (
                    <div className="faint">
                      {opened.scope_location_ids.map((id) => locationName(id) ?? '—').join(', ') ||
                        'no clinics chosen'}
                    </div>
                  )}
                </dd>
              </div>
              <div>
                <dt>Home clinic</dt>
                <dd>{locationName(opened.home_location_id) ?? '—'}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>Department</h3>
            <ErrorNote error={setDepartment.error} />
            {can('org.user.configure') ? (
              <Field label="Works in" caption="defaults what they raise, and so who approves it">
                <select
                  value={opened.department_id ?? ''}
                  disabled={setDepartment.isPending}
                  onChange={(e) =>
                    setDepartment.mutate({ user: opened, departmentId: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {(departments.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="muted">{departmentName(opened.department_id) ?? '—'}</div>
            )}
          </section>

          <section>
            <h3>Password</h3>
            <ErrorNote error={reissue.error ?? reset.error} />
            {opened.must_change_password ? (
              <>
                <p className="muted" style={{ marginTop: 0 }}>
                  Still on the first password, so whoever set it can sign in as them until they
                  choose their own.
                </p>
                {can('org.user.configure') && (
                  <button
                    disabled={reissue.isPending}
                    onClick={() => reissue.mutate({ user: opened, password: generatePassword() })}
                  >
                    Issue another first password
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="muted" style={{ marginTop: 0 }}>
                  Their own. Reset it only when they have forgotten it: the reason is recorded and
                  shown to them, they are signed out everywhere, and they must choose a new one on
                  first use.
                </p>
                {can('org.user.configure') && opened.id !== me?.user_id && opened.is_active && (
                  <div className="row">
                    <Field label="Why" caption="written to the audit trail and shown to them">
                      <input
                        value={resetReason}
                        onChange={(e) => setResetReason(e.target.value)}
                        placeholder="forgot it after leave"
                      />
                    </Field>
                    <div className="shrink">
                      <button
                        disabled={reset.isPending || !resetReason.trim()}
                        onClick={() =>
                          reset.mutate({
                            user: opened,
                            password: generatePassword(),
                            reason: resetReason.trim(),
                          })
                        }
                      >
                        Reset password
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {can('org.role.assign') && (
            <section>
              <h3>
                Change role <span className="hint">· takes effect on their next request</span>
              </h3>
              <ErrorNote error={changeRole.error} />
              {changeRole.isSuccess && <Note kind="ok">Changed. Their session has ended.</Note>}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  changeRole.mutate()
                }}
              >
                <div className="row">
                  <Field label="Role">
                    <select value={nextRole} onChange={(e) => setNextRole(e.target.value)}>
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Row visibility" caption="independent of role">
                    <select
                      value={nextVisibility}
                      onChange={(e) => setNextVisibility(e.target.value)}
                    >
                      {VISIBILITIES.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Reason" caption="recorded in the audit trail; not optional">
                  <input value={reason} onChange={(e) => setReason(e.target.value)} required />
                </Field>
                <Note kind="info">
                  Their existing session is invalidated immediately. A privilege change that waits
                  for a logout is a privilege change that did not happen.
                </Note>
                <button
                  className="primary"
                  type="submit"
                  disabled={
                    changeRole.isPending ||
                    (nextRole === opened.role && nextVisibility === opened.visibility)
                  }
                >
                  {changeRole.isPending ? 'Applying…' : 'Apply change'}
                </button>
              </form>
            </section>
          )}
        </SidePanel>
      )}

      {adding && can('org.user.configure') && (
        <SidePanel
          kicker="People"
          title="Add somebody"
          sub="they set their own password on first sign-in"
          wide
          onClose={() => setAdding(false)}
        >
          <AddPerson locations={locations.data ?? []} />
        </SidePanel>
      )}
    </>
  )
}

function PasswordBanner({ reissued }: { reissued: { user: User; password: string } }) {
  return (
    <Note kind="ok">
      <strong>{reissued.user.display_name}</strong> can sign in as{' '}
      <span className="mono">{reissued.user.email}</span> with the one-time password{' '}
      <span className="mono">
        <strong>{reissued.password}</strong>
      </span>
      . Any earlier one has stopped working, and so has any session opened with it.
    </Note>
  )
}
